import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import puntosIcon from "../assets/puntos.webp";
import iconoMinorista from "../assets/landing/icono_minorista.webp";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "http://localhost:89").replace(/\/$/, "");

const formatCurrency = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const parseJsonResponse = async (response, fallbackMessage) => {
  const contentType = response.headers.get("content-type") || "";
  const text = await response.text();
  if (!response.ok || !contentType.includes("application/json")) {
    throw new Error(fallbackMessage);
  }
  return JSON.parse(text);
};

const normalizePreset = (preset) => {
  const label = typeof preset?.label === "string" ? preset.label.trim() : "";
  const iconUrl = typeof preset?.icon_url === "string" ? preset.icon_url.trim() : "";
  const preloadUrl = typeof preset?.preload_url === "string" ? preset.preload_url.trim() : "";

  if (!label || !iconUrl || !preloadUrl) {
    return null;
  }

  return {
    id: preset?.id ?? `${label}-${preloadUrl}`,
    label,
    icon_url: iconUrl,
    preload_url: preloadUrl,
  };
};

const PresetButton = ({ label, iconUrl, href, fallback = false }) => {
  const sharedClassName =
    "flex min-h-[112px] flex-col items-center justify-between rounded-[18px] border border-[#173b17] bg-[#36c83d] px-3 py-3 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.16)] transition-transform hover:-translate-y-0.5";
  const iconWrapperClassName = fallback ? "h-14 w-14" : "h-14 w-full";
  const iconClassName = fallback ? "h-full w-full object-contain" : "max-h-full max-w-[62px] object-contain";

  const content = (
    <>
      <div className={`flex items-center justify-center ${iconWrapperClassName}`}>
        <img src={iconUrl} alt="" className={iconClassName} />
      </div>
      <span className="mt-3 block text-base font-bold leading-none text-white">{label}</span>
    </>
  );

  if (/^\/(?!\/)/.test(href)) {
    return (
      <Link to={href} className={sharedClassName}>
        {content}
      </Link>
    );
  }

  return (
    <a href={href} className={sharedClassName}>
      {content}
    </a>
  );
};

const Ecommerce = () => {
  const [kitPrice, setKitPrice] = useState(null);
  const [kitPresets, setKitPresets] = useState([]);
  const [separatePrices, setSeparatePrices] = useState({
    fibra: 303,
    resina: 428,
    flakes: 650,
  });

  useEffect(() => {
    let active = true;

    const loadData = async () => {
      try {
        const optionsResponse = await fetch(`${API_BASE_URL}/api/ecommerce/kit-omova/options`);
        const options = await parseJsonResponse(optionsResponse, "No se pudieron cargar opciones de kit.");
        if (!active) return;

        const presets = Array.isArray(options?.presets)
          ? options.presets.map(normalizePreset).filter(Boolean).slice(0, 3)
          : [];

        setKitPresets(presets);

        const firstFiber = Array.isArray(options?.fibers) ? options.fibers[0] : null;
        const firstResin = Array.isArray(options?.resins) ? options.resins[0] : null;

        if (firstFiber && firstResin) {
          const pricingResponse = await fetch(`${API_BASE_URL}/api/ecommerce/kit-omova/pricing`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              width_cm: 30,
              height_cm: 20,
              fiber_code: firstFiber.code,
              resin_id: firstResin.id,
              include_black_resin: false,
              flake_id: null,
            }),
          });
          const pricing = await parseJsonResponse(pricingResponse, "No se pudo calcular kit base.");
          if (active) {
            setKitPrice(Number(pricing?.final_cost) || null);
          }

          const fiberPricingResponse = await fetch(`${API_BASE_URL}/api/ecommerce/fibra-carbono/pricing`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              width_cm: 30,
              height_cm: 20,
              fiber_code: firstFiber.code,
            }),
          });
          const fiberPricing = await parseJsonResponse(
            fiberPricingResponse,
            "No se pudo calcular precio de fibra."
          );
          if (active && Number.isFinite(Number(fiberPricing?.final_cost))) {
            setSeparatePrices((prev) => ({ ...prev, fibra: Number(fiberPricing.final_cost) }));
          }
        }

        const resinsResponse = await fetch(`${API_BASE_URL}/api/ecommerce/resins/products`);
        const resins = await parseJsonResponse(resinsResponse, "No se pudo calcular precio de resinas.");
        if (active && Array.isArray(resins)) {
          const presentationPrices = resins.flatMap((resin) =>
            Array.isArray(resin.presentations)
              ? resin.presentations.map((presentation) => Number(presentation.price)).filter(Number.isFinite)
              : []
          );
          if (presentationPrices.length) {
            setSeparatePrices((prev) => ({
              ...prev,
              resina: Math.min(...presentationPrices),
            }));
          }
        }

        const flakesResponse = await fetch(`${API_BASE_URL}/api/ecommerce/flakes/products`);
        const flakes = await parseJsonResponse(flakesResponse, "No se pudo calcular precio de flakes.");
        if (active && Array.isArray(flakes)) {
          const flakePrices = flakes
            .map((flake) => Number(flake.base_price))
            .filter((price) => Number.isFinite(price));
          if (flakePrices.length) {
            setSeparatePrices((prev) => ({ ...prev, flakes: Math.min(...flakePrices) }));
          }
        }
      } catch (_error) {
        // Mantener fallback visual cuando una parte del cálculo no esté disponible.
      }
    };

    loadData();

    return () => {
      active = false;
    };
  }, []);

  const kitPriceLabel = useMemo(() => {
    if (!Number.isFinite(kitPrice)) return "$470 MXN";
    return `${formatCurrency.format(kitPrice)} MXN`;
  }, [kitPrice]);

  const recommendedPresets = useMemo(
    () =>
      kitPresets.length
        ? [
            ...kitPresets,
            {
              id: "otro",
              label: "Otro",
              icon_url: puntosIcon,
              preload_url: "/ecommerce/kit-omova",
              fallback: true,
            },
          ]
        : [],
    [kitPresets]
  );

  return (
    <main className="bg-[#efefef] px-3 pb-6 pt-4 md:px-8 md:pb-10 md:pt-8">
      <div className="mx-auto max-w-[900px] overflow-hidden rounded-2xl border border-[#d4d4d4] bg-[#f4f4f4]">
        <section className="bg-[radial-gradient(circle_at_top,#0e3036_0%,#071820_55%,#041117_100%)] px-4 pb-4 pt-3 text-white md:px-6 md:pb-5 md:pt-4">
          <p className="text-[11px] uppercase tracking-wide text-white/70 md:text-xs">
            Fibra y resina por medida · hasta 3 m²
          </p>
          <h1 className="mt-1 text-3xl font-extrabold leading-tight md:text-4xl">
            Compra justo lo que necesitas.
          </h1>
          <p className="mt-1 text-sm text-white/85 md:text-base">
            Arma tu kit, te calculamos las cantidades exactas y te llega listo para usar.
          </p>
        </section>

        <section className="grid grid-cols-3 divide-x divide-[#a8d5ef] border-b border-t border-[#cde4f3] bg-[#f3f8fc] px-3 py-2 text-center text-[11px] text-[#4f7590] md:px-6 md:text-sm">
          <div>620+ clientes</div>
          <div>Envío rastreado</div>
          <div>Facturamos</div>
        </section>

        <section className="px-3 py-4 md:px-6 md:py-5">
          <div className="relative overflow-hidden rounded-2xl border border-[#9ea89d] bg-[#edf7ed] p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)] md:p-5">
            <span className="absolute right-3 top-2 rounded-full bg-[#35bf3e] px-2 py-1 text-[10px] font-bold uppercase text-white md:text-xs">
              Más comprado
            </span>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-[1.1fr_0.9fr] md:items-start">
              <div>
                <h2 className="text-[30px] font-extrabold leading-none text-[#35b13f] md:text-[48px]">
                  Kit fibra Omova
                </h2>
                <p className="mt-4 max-w-[420px] text-[24px] leading-[1.18] text-[#222] md:text-[22px]">
                  Para forrar piezas, fabricar partes nuevas o decorar superficies.
                </p>
              </div>
              <div className="flex justify-center md:justify-end">
                <img
                  src={iconoMinorista}
                  alt="Kit Omova"
                  className="h-[140px] w-full max-w-[260px] object-contain md:h-[160px] md:max-w-[310px]"
                />
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-[#8fd78f] pt-3">
              <span className="text-[22px] font-medium text-[#38b247] md:text-[18px]">Desde 30 x 20 cm</span>
              <Link
                to="/ecommerce/kit-omova"
                className="text-[22px] font-extrabold leading-none text-[#20a92f] md:text-[18px]"
              >
                {kitPriceLabel} <span aria-hidden="true">→</span>
              </Link>
            </div>

            {recommendedPresets.length ? (
              <div className="mt-4">
                <p className="text-lg font-semibold text-[#666] md:text-[16px]">
                  Kits recomendados para proyectos:
                </p>
                <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                  {recommendedPresets.map((preset) => (
                    <PresetButton
                      key={preset.id}
                      label={preset.label}
                      iconUrl={preset.icon_url}
                      href={preset.preload_url}
                      fallback={preset.fallback}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-4">
                <Link
                  to="/ecommerce/kit-omova"
                  className="inline-flex items-center rounded-xl bg-[#36c83d] px-5 py-3 text-lg font-bold text-white"
                >
                  Personalizar kit <span className="ml-2" aria-hidden="true">→</span>
                </Link>
              </div>
            )}
          </div>

          <div className="mt-5 border-t border-[#d5d5d5] pt-4">
            <p className="text-xs uppercase tracking-[0.2em] text-[#666] md:text-sm">O compra por separado</p>
            <div className="mt-3 space-y-2">
              <Link
                to="/ecommerce/fibra-carbono"
                className="flex items-center justify-between rounded-xl border border-[#c8c8c8] bg-white px-3 py-2"
              >
                <div className="flex items-center gap-3">
                  <img src="/ecommerce/Fibras.png" alt="Fibra" className="h-10 w-10 object-contain" />
                  <div>
                    <p className="text-lg font-bold text-[#1e1e1e] md:text-xl">Fibra de carbono</p>
                    <p className="text-xs text-[#6b6b6b]">11 tipos · desde 30 x 20 cm</p>
                  </div>
                </div>
                <p className="text-3xl font-semibold text-[#2a2a2a] md:text-4xl">
                  {formatCurrency.format(separatePrices.fibra)}+
                </p>
              </Link>

              <Link
                to="/ecommerce/resina-epoxica"
                className="flex items-center justify-between rounded-xl border border-[#c8c8c8] bg-white px-3 py-2"
              >
                <div className="flex items-center gap-3">
                  <img src="/ecommerce/Resinas.png" alt="Resina" className="h-10 w-10 object-contain" />
                  <div>
                    <p className="text-lg font-bold text-[#1e1e1e] md:text-xl">Resina</p>
                    <p className="text-xs text-[#6b6b6b]">11 tipos · desde 250 g</p>
                  </div>
                </div>
                <p className="text-3xl font-semibold text-[#2a2a2a] md:text-4xl">
                  {formatCurrency.format(separatePrices.resina)}+
                </p>
              </Link>

              <Link
                to="/ecommerce/flakes"
                className="flex items-center justify-between rounded-xl border border-[#c8c8c8] bg-white px-3 py-2"
              >
                <div className="flex items-center gap-3">
                  <img src="/ecommerce/Flakes.png" alt="Flakes" className="h-10 w-10 object-contain" />
                  <div>
                    <p className="text-lg font-bold text-[#1e1e1e] md:text-xl">Flakes</p>
                    <p className="text-xs text-[#6b6b6b]">7 colores disponibles</p>
                  </div>
                </div>
                <p className="text-3xl font-semibold text-[#2a2a2a] md:text-4xl">
                  {formatCurrency.format(separatePrices.flakes)}+
                </p>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Ecommerce;
